import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { getInfoTooltipHeaderCell } from 'components/Shared/MuiTable/Cell/InfoTooltipHeaderCell'
import { useNewTranslation } from 'hooks'
import { CampaignState } from 'state/campaigns'
import { generateUuid } from 'services/uuid'
import { convertToThousandSeparator } from 'components/utils'

export interface CampaignPerformanceRowProps {
    id: string
    uuid: string
    task: {
        value: string
    }
    followers: {
        value: string
    }
    reach: {
        value: string
    }
    views: {
        value: string
    }
    engagement: {
        value: string
    }
    engagementRate: {
        value: string
    }
    contentEngagementRate: {
        value: string
    }
}

interface CampaignPerformanceData {
    follower_total: number | null
    tasks: {
        uuid: string
        title: string
        follower: number | null
        performance: {
            reach: number | null
            view: number | null
            engagement: number | null
            engagement_rate: number | null
            content_engagement_rate: number | null
        }
    }[]
    performance_total: {
        reach: number | null
        view: number | null
        engagement: number | null
        engagement_rate: number | null
        content_engagement_rate: number | null
    }
}

export const getCampaignPerformanceColumnsDef = (): GridColDef[] => {
    const [t] = useNewTranslation()

    return [
        {
            field: 'task',
            headerName: t('submission.campaignReport.task.header'),
            width: 240,
            resizable: false,
            headerClassName: 'align-left-header',
            align: 'left',
            cellClassName: 'title-cell',
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            sortable: false,
            filterable: false,
        },
        {
            field: 'followers',
            headerName: t('submission.campaignReport.metric.totalFollowers'),
            minWidth: 120,
            align: 'center',
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            flex: 1,
        },
        {
            field: 'reach',
            headerName: t('submission.table.reachLabel'),
            renderHeader: getInfoTooltipHeaderCell({
                width: 'auto',
                description: t('submission.table.reachTooltips'),
                textColor: 'primary',
                iconColor: 'primary',
                textAlign: 'center',
            }),
            minWidth: 120,
            align: 'center',
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            sortable: false,
            filterable: false,
            flex: 1,
        },
        {
            field: 'views',
            headerName: t('submission.table.viewsLabel'),
            minWidth: 120,
            align: 'center',
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            flex: 1,
        },
        {
            field: 'engagement',
            headerName: t('submission.table.engagementLabel'),
            renderHeader: getInfoTooltipHeaderCell({
                width: 'auto',
                description: t('campaignReport.content.tooltip.totalEngagementDescription'),
                textColor: 'primary',
                iconColor: 'primary',
                textAlign: 'center',
                options: {
                    truncateText: true,
                },
            }),
            minWidth: 120,
            align: 'center',
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            flex: 1,
        },
        {
            field: 'engagementRate',
            headerName: t('submission.table.engagementRateLabel'),
            renderHeader: getInfoTooltipHeaderCell({
                width: 'auto',
                description: t('submission.table.engagementRateTooltips'),
                textColor: 'primary',
                iconColor: 'primary',
                textAlign: 'center',
                options: {
                    truncateText: true,
                },
            }),
            minWidth: 120,
            align: 'center',
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            flex: 1,
        },
        {
            field: 'contentEngagementRate',
            headerName: t('submission.table.contentEngagementRateLabel'),
            renderHeader: getInfoTooltipHeaderCell({
                width: 'auto',
                description: t('submission.table.contentEngagementRateTooltips'),
                textColor: 'primary',
                iconColor: 'primary',
                textAlign: 'center',
                options: {
                    truncateText: true,
                },
            }),
            minWidth: 120,
            align: 'center',
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            flex: 1,
        },
    ]
}

export const getCampaignPerformanceRowsDef = (
    data: CampaignPerformanceData,
    campaign: CampaignState,
): CampaignPerformanceRowProps[] => {
    const [t] = useNewTranslation()
    const rows = data?.tasks?.map((task, index) => ({
        id: task.uuid,
        uuid: task.uuid,
        task: { value: task.title },
        followers: { value: convertToThousandSeparator(task?.follower) },
        reach: { value: convertToThousandSeparator(task.performance?.reach) },
        views: { value: convertToThousandSeparator(task.performance?.view) },
        engagement: {
            value: convertToThousandSeparator(task.performance?.engagement),
        },
        engagementRate: {
            value: task.performance?.engagement_rate
                ? `${convertToThousandSeparator(task.performance.engagement_rate)}%`
                : '',
        },
        contentEngagementRate: {
            value: task.performance?.content_engagement_rate
                ? `${convertToThousandSeparator(task.performance.content_engagement_rate)}%`
                : '',
        },
    }))

    const performanceTotal = data?.performance_total

    const totalRowUuid = generateUuid()

    const totalRow = {
        id: totalRowUuid,
        uuid: totalRowUuid,
        task: { value: t('submission.campaignReport.totalRow.title') },
        followers: { value: convertToThousandSeparator(data?.follower_total) },
        reach: { value: convertToThousandSeparator(performanceTotal?.reach) },
        views: { value: convertToThousandSeparator(performanceTotal?.view) },
        engagement: { value: convertToThousandSeparator(performanceTotal?.engagement) },
        engagementRate: {
            value: performanceTotal?.engagement_rate
                ? `${convertToThousandSeparator(performanceTotal?.engagement_rate)}%`
                : '',
        },
        contentEngagementRate: {
            value: performanceTotal?.content_engagement_rate
                ? `${convertToThousandSeparator(performanceTotal?.content_engagement_rate)}%`
                : '',
        },
    }

    return [...rows, totalRow]
}
